import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useAuthUser } from "@features/auth/AuthUserContext";
import { currentUserKeyFactory } from "@features/auth/data/currentUserQueries";
import { Organization } from "@models/Organization";
import client, { useMutateError } from "@services/api";
import { QueryOptions } from "@utils/reactQuery";

import type { CreateOrganizationPayload } from "../create/formData";

export const organizationQueryKeyFactory = createQueryKeys("organizations", {
  detail: (organizationId: string) => ({
    queryKey: [organizationId],
    queryFn: () =>
      client
        .get<Organization>(`organizations/${organizationId}`)
        .then((res) => res.data),
  }),
  list: () => ({
    queryKey: ["list"],
    queryFn: () =>
      client.get<Organization[]>("organizations").then((res) => res.data),
  }),
});

// Initialize from the user's organization, but enables refetching from the server.
export const useOrganization = (): Organization => {
  const { organization: authUserOrganization } = useAuthUser();
  const { data: organization } = useQuery({
    ...organizationQueryKeyFactory.detail(authUserOrganization.id),
    initialData: authUserOrganization,
  });
  return organization;
};

export const useOrganizationsQuery = (
  options?: QueryOptions<Organization[]>
) => {
  return useQuery({
    ...organizationQueryKeyFactory.list(),
    placeholderData: keepPreviousData,
    staleTime: 60 * 60_000,
    ...options,
  });
};

export const useCreateOrganizationMutation = () => {
  const queryClient = useQueryClient();
  const setMutateError = useMutateError();
  return useMutation({
    mutationFn: (attributes: CreateOrganizationPayload) =>
      client
        .post<Organization>("organizations", {
          ...attributes,
          __type: "organization",
        })
        .then((res) => res.data),
    onError: (error) => setMutateError(error, "Create Organization"),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organizationQueryKeyFactory._def,
      });
    },
  });
};

type UpdateOrganizationPayload = { id: string } & Partial<Organization>;

const INVALID_TERRITORY_ERROR_MESSAGE =
  "A territory must set at least one checkout type";
const TERRITORY_CHECKOUT_TYPE_FRIENDLY_ERROR_MESSAGE =
  "This change would leave one or more territories without a checkout type. " +
  "Each territory must have at least one checkout type enabled. Assign another before disabling this one.";

export const useUpdateOrganizationMutation = () => {
  const queryClient = useQueryClient();
  const setMutateError = useMutateError();
  return useMutation({
    mutationFn: ({ id, ...attributes }: UpdateOrganizationPayload) =>
      client
        .put<Organization>(`organizations/${id}`, {
          ...attributes,
          __type: "organization",
        })
        .then((res) => res.data),
    onError: (error) => {
      if (
        "message" in error &&
        error.message.includes(INVALID_TERRITORY_ERROR_MESSAGE)
      ) {
        setMutateError(
          new Error(TERRITORY_CHECKOUT_TYPE_FRIENDLY_ERROR_MESSAGE),
          "Update Organization"
        );
        return;
      }
      setMutateError(error, "Update Organization");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: organizationQueryKeyFactory._def,
      });
      queryClient.invalidateQueries({
        queryKey: currentUserKeyFactory.detail.queryKey,
      });
    },
  });
};
